import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Shield, AlertTriangle } from "lucide-react";

const RoleGuard = ({
  children,
  requiredRole,
  fallback = null,
  showAccessDenied = true,
}) => {
  const { user, isAuthenticated, hasRole, hasMinimumRole } = useAuth();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If no user data yet, show loading
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if user has the required role or minimum role
  const hasAccess = requiredRole
    ? hasRole(requiredRole) || hasMinimumRole(requiredRole)
    : true;

  if (!hasAccess) {
    if (fallback) {
      return fallback;
    }

    if (showAccessDenied) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-red-800">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5" />
                  <span className="font-semibold">Access Denied</span>
                </div>
                <p className="text-sm">
                  You don't have permission to access this page. This area is
                  restricted to{" "}
                  {requiredRole === "super_admin"
                    ? "Super Administrators"
                    : "authorized users"}
                  .
                </p>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      );
    }

    return null;
  }

  return children;
};

export default RoleGuard;
